import React from 'react';
import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import { H3, H4, H5 } from '@/components/Element/Heading';
import MetaTags from '@/components/Head/MetaTags';
// import SimpleCenteredHeaderSection from '@/components/HeaderSection/SimpleCenteredHeaderSection';
import SideCardContent from '@/components/SideCardContent/SideCardContent';
import SideCardCTA from '@/components/SideCardCTA/SideCardCTA2';
import Link from 'next/link';
import FullWidthGuideFormSection from '@/components/LeadForm/FullWidthGuideForm';
import Image1 from 'images/solar-guide/image33.jpg';
import Image2 from 'images/solar-guide/image43.png';
import Image3 from 'images/solar-guide/image69.png';
import Image4 from 'images/solar-guide/image30.png';
import Image5 from 'images/solar-guide/image14.png';
import Image6 from 'images/solar-guide/image7.png';
import Image from 'next/legacy/image';
import ReadingTime from '@/components/ReadingTime/ReadingTime';
import { SolarGuideTable } from './why-go-solar';
const Page = () => {
  return (
    <article>
      <MetaTags
        title="💸 Economics of Home Solar"
        description="2023 Home Solar Guide"
      />
      <FullWidthBarBreadcrumbs
        pages={[
          {
            name: 'Home Solar Guide',
            href: '/home-solar-guide',
          },
          {
            name: '💸 Economics of Home Solar',
            href: '/home-solar-guide/economics-of-home-solar',
          },
        ]}
      />
      <CenteredContentSection>
        <section id="section1">
          <div className="flex justify-between items-center">
            <Link
              href={'/home-solar-guide/steps-to-install-a-home-solar-system'}
            >
              ← Steps to Install a Home Solar System
            </Link>{' '}
            <Link href={'/home-solar-guide/purchase-and-financing-options'}>
              Purchase & Financing Options →
            </Link>
          </div>
          <H3>💸 Economics of Home Solar</H3>
          <ReadingTime wordCount={2570} />
          <p>
            Fundamentally, there are two ways that you can save money in your
            home. You can either consume less or buy cheaper electricity. Aside
            from solar, other home upgrades focus on consuming less energy. This
            could be changing windows, adding increased insulation, or upgrading
            a heating or cooling system. These upgrades can add additional home
            comfort, improve air quality, and help conserve electricity or gas
            consumption. However you will still need to buy a certain amount of
            electricity every month from your local electric utility company.
          </p>
          <p>
            Solar is a viable alternative, whether paid upfront or over time. At
            an effective price per kilowatt-hour solar power allows you to pay
            less than what you would pay your local utility company.
          </p>
          <p>
            The question many ask is “
            <Link href="/solar-faq/how-much-can-i-save-with-solar-energy">
              How much can I save?”
            </Link>{' '}
            and{' '}
            <Link href="/solar-faq/how-much-do-solar-panels-cost">
              How much does it cost?”
            </Link>
            “
          </p>
          <p>
            To answer these questions, we must first understand how much you are
            currently using, what rate per kilowatt hour the you are paying, how
            much solar power could be produced on your property, how the utility
            company interacts with solar power on a daily, monthly, and yearly
            basis and what other financial incentives may be available.
          </p>
          <p>Let’s start with the electric bill.</p>
          <H4>How to Read an Electric Bill</H4>
          <Image src={Image1} alt="Electric Bill" />
          <p>
            You might not give your electricity bill much thought — except
            perhaps to lament how high it is — but{' '}
            <strong>
              electricity bills actually provide a lot of valuable information
              to determine if solar is right for you.
            </strong>
          </p>
          <p>
            Having a deeper understanding of the information contained in
            electricity bills can offer insights into whether installing solar
            makes sense, as well as whether switching to another billing plan
            may increase savings.
          </p>
          <p>
            Let’s take a look at the terms, sections, and calculations in an
            electricity bill and how these change when they install solar.
          </p>
          <Image src={Image2} alt="Electric Bill Rate Plans" />
          <H4>Rate Plans</H4>
          <p>
            An electricity bill is a charge for the electricity a home or
            business consumes. This consumption is measured in units called
            kilowatt-hours (kWh) and customers are charged a per-kWh rate. These
            rates differ across utilities and are calculated differently
            depending on your“rate plan.” Rate plans specify the rules for how
            bills are calculated and utilities typically offer multiple plans.
          </p>
          <p>
            <strong>
              Common rate plans include fixed rates, time of use (TOU) rates,
              and tiered rates.
            </strong>{' '}
            Fixed rate plans charge the same amount for every kWh consumed,
            while with TOU rates and tiered rates the price per kWh changes
            depending on the time of day (peak vs. off-peak) or the total amount
            of energy consumed, respectively. There are also demand rate plans
            which charge you primarily on the maximum amount of appliances being
            run simultaneously.
          </p>
          <p>
            Your rate plan will determine what’s displayed in the different
            sections of your bill and it also impacts how much you pay.
            Depending on your energy consumption patterns you may pay more or
            less for the same amount of energy under different plans.
          </p>
          <H4>Utility Bill Sections</H4>
          <p>
            An electricity bill is broken down into several different sections,
            and while the names and contents of each section often differ
            depending on the utility here are some of the most common sections:
          </p>
          <H5>
            <strong>Account Summary</strong>
          </H5>
          <p>
            The Account Summary generally appears on the front and center of the
            bill. This section provides an overview of the account status,
            including the previous account balance, any payments made on the
            previous balance, and the new amount owed for the current billing
            period. If you get electricity and gas from the same utility the
            Account Summary will include charges for both of these services.
          </p>
          <H5>
            <strong>Bill Details</strong>
          </H5>
          <p>
            This section shows the number of kWh they consumed during the
            billing period and the rate they pay per kWh. What’s shown in this
            section and how it’s formatted will change depending on the utility
            and rate plan.
          </p>
          <H5>
            <strong>Electricity Charges Breakdown</strong>
          </H5>
          <p>
            The per-kWh rate shown in the “Bill Details” section is made up of
            many smaller charges. In addition to covering the cost of the energy
            consumed, some of these charges are used to maintain and upgrade the
            electric grid and to fund other state-sponsored energy initiatives.
            The names and amounts of these electricity charges vary by utility
            but generally include a generation, transmission, and distribution
            charge.
          </p>
          <p>
            <strong>Generation Charge:</strong> Supports the cost of producing
            the electricity used.
          </p>
          <p>
            <strong>Transmission Charge:</strong> Supports the cost of
            transmitting electricity from power plants over high-voltage lines
            and towers to the distribution system.
          </p>
          <p>
            <strong>Distribution Charge:</strong> Supports the cost of the
            lower-voltage system of power lines, poles, substations, and
            transformers that connects to homes and businesses. In the bill
            above, you can see this in the “Current Charges for Electricity”
            section, which is broken down into “Supply” and “Delivery.” A base
            customer service charge or delivery fee that does not change will
            also be present in this section. This amount would be charged even
            if the homeowner used zero (0) kilowatt-hours
          </p>
          <H5>
            <strong>Usage Profile</strong>
          </H5>
          <p>
            Many utilities provide a monthly usage profile showing the
            homeowner&apos;s total consumption each month over the past year.
            This gives a good visual representation of how much energy
            homeowners consume throughout the year and sometimes even compares
            the current year&apos;s consumption to that of years past.
          </p>
          <Image src={Image3} alt="Net Metering" />
          <H3>Net Metering</H3>
          <p>
            Once we understand the bill, we can understand how the utility
            company interacts with the solar energy produced on a house. In the
            majority of solar areas there is an incentive program called Net
            Energy Metering (NEM) or Net Metering.{' '}
            <strong>
              Net metering is an electric billing tool that uses the electric
              grid to store excess energy produced by a solar panel system.
            </strong>{' '}
            Under net metering energy solar panels produce and don&apos;t get
            used in the home is credited back to the homeowner. On a cloudy or
            rainy day when your panel&apos;s aren&apos;t producing enough energy
            for the use of the home the utility grid will supply your home
            energy and count that energy against the credits you&apos;ve banked
            over time. As a solar customer you will only be billed for your
            “net” energy usage.
          </p>
          <p>
            This policy is typically enforced by a state government but
            administered through local utility companies. Net metering takes the
            complexity of solar power production and home energy demand and
            makes it <strong>a lot more simple!</strong>
          </p>
          <p>
            Typically all we need to do is identify what is the{' '}
            <strong>total annual kWh</strong> a homeowner uses and match that
            with the annual expected energy their solar panels will produce.
          </p>
          <Image src={Image4} alt="Consumption vs Production chart" />
          <p>
            As you can see from the graph - the solar production doesn’t
            perfectly match when the homeowner needs electricity. That’s OK with
            net metering as the excess (April, May, June, July, August) cover
            the deficiencies during the rest of the year.
          </p>
          <Image src={Image5} alt="Net Metering in Winter vs Summer" />
          <H4>How does Net Metering work?</H4>
          <p>
            Say you install a net metered solar panel system. When your solar
            panels are producing more electricity than they are using at any
            point during the day the{' '}
            <strong>
              excess electricity is sent back to the grid which runs your
              electric meter in reverse.
            </strong>{' '}
            When you use more electricity than your solar panels are producing,
            either at night or on cloudy days, you pull electricity back from
            the grid which runs your meter forwards. At the end of the month or
            year you are billed the net of what you put onto the grid and what
            you took off the grid hence “net metering”.{' '}
          </p>
          <p>
            With a correctly sized solar energy system, you can produce enough
            electricity to match your home&apos;s electricity use for the entire
            year. The amount of electricity your solar panels produce will vary
            throughout the year. Solar will produce more in sunnier summer
            months and less when the sun is lower in the sky and sets earlier in
            the winter. Net metering helps you to account for these seasonal
            differences in solar production by crediting you for the excess
            electricity your panels produce so that you can use it at a later
            date.
          </p>
          <H4>Why does net metering exist?</H4>
          <p>
            Net metering is designed for two primary purposes: first to
            encourage the greater adoption of solar throughout the country and
            second because utilities–and the electricity grid as a whole–can
            benefit from the influx of low- to no-cost solar energy onto the
            grid. Solar energy can help balance the cost of purchasing
            electricity from other resources, especially during summer months
            when electricity is often the most expensive on the hottest–and
            sunniest!–days of the year.{' '}
          </p>
          <H4>How do electricity bills work with net metering?</H4>
          <p>
            In general, most homes will produce excess electricity in the summer
            months and will use more electricity from the grid in the winter.
            These variations in production are fairly predictable so a utility
            company won’t send you a monthly check when you produce more than
            you need but instead{' '}
            <strong>
              you will build up extra credits on your utility account during the
              summer months so that you can draw from them at night and during
              the winter months
            </strong>{' '}
            when you need them. With the right design a system can generate
            enough power to match the total home electricity use for a year even
            if they produce much more than you need in some months and much less
            in others.
          </p>
          <p>
            When your solar power system generates more electricity than you use
            over the course of a month you will receive a credit based on the
            net number of kilowatt-hours they gave back to the grid. If you
            produce less electricity than you use in a given month you must buy
            electricity from your utility to make up the difference. In these
            instances you would pay for the electricity you use minus any excess
            electricity your solar panels generated.
          </p>
          <H4>Use net metering to save by going solar</H4>
          <p>
            Net metering is the best solar policy since it allows you to store
            every unit of energy you produce with solar to be used at a later
            date from the grid. In fact as a result of net metering you can save
            tens of thousands of dollars over the lifetime of your solar panel
            system by offsetting your need for electricity from the grid.
          </p>
          <p>
            While net metering is not the only way that utilities compensate
            homeowners for going solar it is by far the most common and
            effective solar policy.
          </p>
          <H3>Incentives</H3>
          <p>
            To make solar installation more financially competitive versus doing
            nothing and continuing to get power from fossil fuel driven
            utilities federal, state, local and utility incentives have been
            created to foster the adoption of clean energy.
          </p>
          <p>
            There are several ways in which the incentives are structured which
            we cover below. Any incentive typically stimulates customers to look
            into and adopt solar (and/or home batteries) because application of
            the incentives lowers the overall cost of the system regardless if
            the system is purchased outright with cash, financed with a loan, or
            leased from a third-party that receives the incentive.
          </p>
          <p>
            A lot of homeowners decide to go solar specifically because of the
            available incentives.
          </p>
          <p>
            <strong>
              Let us help you navigate the incentives and benefits available to
              you and your home:
            </strong>
          </p>
        </section>
      </CenteredContentSection>
      <FullWidthGuideFormSection cid={'a4oqmxxt5un31ha0'} />
      <CenteredContentSection>
        <Image src={Image6} alt="Solar Investment Tax Credit" />
        <H4>Cost-Based Incentives</H4>
        <p>
          Cost-based incentives allow people or companies who invest in a solar
          system to apply a tax credit towards their income tax. The incentive
          is determined by the cost of the system and is independent of its
          performance.
        </p>
        <p>
          The important cost-based incentive driving solar adoption today is the
          United States Federal Solar Investment Tax Credit (“Solar ITC”). This
          incentive is a function of the total system cost or loan amount for a
          homeowner. You would claim this incentive through your personal tax
          returns. You can also learn more on the{' '}
          <a
            target="_blank"
            href={
              'https://www.energy.gov/eere/solar/homeowners-guide-federal-tax-credit-solar-photovoltaics'
            }
            rel="noreferrer"
          >
            ITC here.
          </a>
        </p>
        <H4>What is a tax credit?</H4>
        <p>
          A tax credit is a dollar-for-dollar reduction in the amount of income
          tax you would otherwise owe. For example, claiming a $1,000 federal
          tax credit reduces your federal income taxes due by $1,000. The
          federal tax credit is sometimes referred to as an Investment Tax
          Credit or “ITC” even though it is different from the ITC offered to
          businesses that own solar systems.
        </p>
        <p>
          Other cost-based incentives are typically regional or state tax
          credits which work in a similar way and vary by market. One of the
          most comprehensive websites with state and local incentives is{' '}
          <a
            target="_blank"
            href="https://programs.dsireusa.org/system/program"
            rel="noreferrer"
          >
            DSIRE.
          </a>
        </p>
        <H4>System Size-Based Incentives</H4>
        <p>
          Many utilities and state governments have system size-based
          incentives.{' '}
          <strong>
            These incentives are calculated based on the kW DC or AC Capacity of
            the system.
          </strong>{' '}
          The idea is the more the solar generation capacity is connected to the
          grid the larger the incentive. Sometimes these incentives have minimum
          performance factors in order to ensure that homeowners receiving these
          incentives aren’t getting money for panels that don’t work very well
          due to shade or orientation.
        </p>
        <p>
          Where cost-based incentives are almost always provided to you after
          your installation is complete, system size-based incentives can
          sometimes be paid before or after installation.
        </p>
        <p>
          When an incentive is claimed and paid to a homeowner (or solar
          financing company) before they are installed the incentive is
          typically routed directly to an installer and the installer reduces
          the contract price of the system accordingly. We call these
          incentives, &ldquo;Discount Incentives&ldquo; as they discount the
          cost of the system and may reduce any cost-based incentives.
        </p>
        <p>
          When an incentive is secured but not paid to a homeowner (or solar
          financing company) until after they are installed, we call this a
          &ldquo;Rebate Incentive&ldquo;. Homeowners typically receive this
          rebate check in the mail (or direct deposit).
        </p>
        <p>
          System size-based rebates are typically presented on a dollar per watt
          basis ($/W). For example, if a utility offered a $1.00/W rebate with a
          10,000 W or 10 kW system, the homeowner would get a $10,000 rebate.
          This could be over 30% of the cost of the system!
        </p>
        <p>
          Similarly, battery system-size rebates are typically presented on a
          dollar per kilowatt-hour basis ($/kWh) so if a utility offered a
          0.30$/Wh rebate on a 10000 Wh or 10 kWh capacity battery system, the
          homeowner would get $3,000.
        </p>
        <p>
          EnergyPal&apos;s platform will automatically calculate size based
          incentives so you can be sure the quotes you get are accurate and
          meaningful.
        </p>
        <H4>Performance-Based Incentives</H4>
        <p>
          <strong>
            Performance-based incentives (PBIs) deliver payments based on the
            monthly energy production of a solar system or the power discharge
            events of a battery.
          </strong>{' '}
          You can make the most of any performance-based incentives in your area
          by installing an efficient system and maintaining it so it keeps
          producing as much as possible.
        </p>
        <p>
          Performance based incentives are usually paid to you on an ongoing
          basis for 5-25 years. The performance-based incentive can be delivered
          to you over varying timelines from monthly, quarterly, bi-annually, or
          yearly.
        </p>
        <p>
          Sometimes solar companies will exchange an upfront sum to you in
          exchange for the performance based incentive rights over time. This
          can be very advantageous for you if you want to minimize upfront costs
          or loan amounts.
        </p>
        <H4>Property Tax Exemptions</H4>
        <p>
          When solar is installed, homeowners typically improve their property
          value. This increase in value can come in two formats: Market Value
          and Appraised Value. Appraised value would be the value a bank would
          give and is the value that property taxes are based off of. Market
          value could be much higher than the appraised value.
        </p>
        <p>
          In many markets solar will increase the appraised value (assuming the
          homeowner owns the system) on their home. Typically this increase
          would also increase the annual property tax the homeowner would owe
          but{' '}
          <strong>
            most states have specific exemptions for property tax increases due
            to solar.
          </strong>{' '}
          This means a home’s appraised value may go up but their property taxes
          would not go up.
        </p>
      </CenteredContentSection>
      <SideCardContent titles={['The Difficulty of Installation']} />
      <SideCardCTA />
      <FullWidthGuideFormSection cid={'a4oqmxxt5un31ha0'} />
      <SolarGuideTable />
    </article>
  );
};

export default Page;
